import type { CSSProperties, FocusEvent, HTMLAttributes, MouseEvent as ReactMouseEvent } from 'react';
import { forwardRef, useEffect, useRef } from 'react';

import { useModifiers } from '../../../styles/hooks/useModifiers';

import styles from './RangeInput.module.css';

export interface RangeInputProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange' | 'onDrag' | 'onKeyDown'> {
  max: number;
  min: number;
  onChange: (value: number) => void;
  onDrag: (value: number) => void;
  step: number;
  value: number;
  disabled?: boolean;
  onDragEnd?: () => void;
  onDragStart?: () => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  vertical?: boolean;
}

const RangeInput = forwardRef<HTMLDivElement, RangeInputProps>(function RangeInputFunc(
  { value, min, max, onChange, onDrag, onDragStart, onDragEnd, step, onKeyDown, vertical, disabled, ...rest },
  handleRef
) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef<number>(value);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const valueToProgress = (value: number): number => (value - min) / (max - min);
  const progressToValue = (progress: number): number => progress * (max - min) + min;

  const roundToStep = (value: number, step: number): number => {
    return Math.round(value / step) * step;
  };

  const handleDrag = (event: ReactMouseEvent<HTMLDivElement>): void => {
    if (disabled) {
      return;
    }
    const { clientY, clientX } = event;

    const wrapperBounds = wrapperRef.current?.getBoundingClientRect();
    const sliderSize = (vertical ? wrapperBounds?.height : wrapperBounds?.width) ?? 0;
    // minus origin so that logic won't break on transforms
    let lastDragPosition = vertical ? clientY - (wrapperBounds?.top || 0) : clientX - (wrapperBounds?.left || 0);

    onDragStart?.();

    const clamp = (value: number, firstBoundary: number, secondBoundary: number): number => {
      if (firstBoundary < secondBoundary) {
        return Math.min(Math.max(value, firstBoundary), secondBoundary);
      }
      return Math.min(Math.max(value, secondBoundary), firstBoundary);
    };

    const getProgressFromPosition = (): number => {
      return vertical ? 1 - lastDragPosition / sliderSize : lastDragPosition / sliderSize;
    };

    const handleMove = (event: PointerEvent): void => {
      const position = vertical
        ? event.clientY - (wrapperBounds?.top ?? 0)
        : event.clientX - (wrapperBounds?.left ?? 0);
      lastDragPosition = clamp(position, 0, sliderSize);
      const value = progressToValue(getProgressFromPosition());
      const result = value === max ? max : roundToStep(value, step);
      onDrag(result);
    };

    handleMove(event as never);

    window.addEventListener('pointermove', handleMove, { passive: true });

    const handlePointerUp = (): void => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('touchend', handlePointerUp);

      const value = progressToValue(getProgressFromPosition());
      const result = value === max ? max : roundToStep(value, step);
      onChange(result);
      onDragEnd?.();
    };

    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('touchend', handlePointerUp);
  };

  const handleFocus = (event: FocusEvent<HTMLDivElement>): void => {
    const { target } = event;

    const performStep = (scale: 1 | -1): void => {
      const fullSteps = Math.round(valueRef.current / step);
      const newValue = fullSteps * step + step * scale;
      onChange(Math.min(Math.max(newValue, min), max));
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        performStep(-1);
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        performStep(1);
      } else if (event.key === 'Home') {
        onChange(min);
      } else if (event.key === 'End') {
        onChange(max);
      }
      onKeyDown?.(event);
    };
    target.addEventListener('keydown', handleKeyDown);
    const handleBlur = (): void => {
      target.removeEventListener('keydown', handleKeyDown);
      target.removeEventListener('blur', handleBlur);
    };
    target.addEventListener('blur', handleBlur);
  };

  const mods = useModifiers(styles, 'rangeInput', { disabled, vertical }, { withBaseClass: true });

  return (
    <div className={mods} {...rest}>
      <div className={styles.rangeInput__sliderYardStick} ref={wrapperRef} />
      <div
        className={styles.rangeInput__sliderTrack}
        style={{ '--sliderProgress': valueToProgress(value) } as CSSProperties}
        onPointerDown={handleDrag}
      >
        <div className={styles.rangeInput__sliderProgress} />
        <div
          className={styles.rangeInput__sliderThumb}
          aria-valuemax={max}
          aria-valuemin={min}
          aria-valuenow={value}
          onFocus={handleFocus}
          ref={handleRef}
          role="slider"
          tabIndex={disabled ? -1 : 0}
        />
      </div>
    </div>
  );
});

RangeInput.displayName = 'RangeInput';

export { RangeInput };
